import {
  Box,
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CLIENT_CONFIG } from "../../core/client/buildConfig";
import { useLanguage } from "../../hooks/utility/useLanguage";
import { AnswerFeedback } from "../../types/chatTypes";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: AnswerFeedback) => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useLanguage();
  const [reason, setReason] = useState<string>("");
  const [feedback, setFeedback] = useState<string>("");
  const [feedBackIsSent, setFeedbackIsSent] = useState<boolean>(false);

  const reasons = useMemo(() => {
    return [] as string[]
  }, []);

  const feedbackPlaceholder = t('feedback.enterFeedback')

  // assistantName is optional in a client's branding config, so fall back to a
  // generic term to keep the disclaimer well-formed when it's absent.
  const branding: { assistantName?: string; companyName: string } =
    CLIENT_CONFIG.theme.branding
  const disclaimer = t('feedback.downvoteDisclaimer', {
    assistant: branding.assistantName || t('feedback.assistantFallback'),
  })

  useEffect(() => {
    setReason("");
    setFeedback("");
    setFeedbackIsSent(false);
  }, [isOpen]);

  const onSubmitFeedback = useCallback(() => {
    setFeedbackIsSent(true);
    onSubmit({ reason, feedback });
    setTimeout(() => {
      onClose()
    }, 1000);
  }, [onSubmit, onClose, reason, feedback]);


  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "md" }}>
      <ModalOverlay />
      <ModalContent borderRadius={0} h={{ base: "100dvh", md: "auto" }} minH={{ base: "100dvh", md: "auto" }}>
        <ModalHeader>
          <ModalCloseButton alignSelf="flex-start" />
        </ModalHeader>
        {feedBackIsSent ? (
          <ModalBody>
            <Text mb={4} fontSize="2xl" fontWeight="bold">
              {t('feedback.feedbackSent')}
            </Text>
          </ModalBody>
        ) : (
          <>
            <ModalBody>
              <Text fontSize="2xl" fontWeight="bold">
                {t('feedback.provideFeedback')}
              </Text>
              <Box mt={3} p={3} bg="gray.50" borderRadius="md">
                <Text fontSize="sm" color="gray.700">
                  {disclaimer}
                </Text>
              </Box>
              {reasons.length > 0 && (
                <Grid mt={4} templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                  {reasons.map((r) => (
                    <Button
                      key={r}
                      onClick={() => setReason(r)}
                      borderRadius={16}
                      fontSize="sm"
                      textTransform="none"
                      justifyContent="flex-start"
                      fontWeight="light"
                      bg={r === reason ? "brand.500" : "gray.200"}
                      color={r === reason ? "white" : "black"}
                      _hover={{ bg: "brand.500", color: "white" }}
                    >
                      {r}
                    </Button>
                  ))}
                </Grid>
              )}
              <Textarea
                mt={4}
                placeholder={feedbackPlaceholder}
                resize="none"
                overflow="auto"
                onChange={(event) => setFeedback(event.target.value)}
              />
            </ModalBody>
            <ModalFooter justifyContent="center">
              <Button
                color="white"
                bg="brand.500"
                _hover={{
                  bg: "white",
                  color: "brand.500",
                  border: "1px solid",
                  borderColor: "brand.500",
                }}
                variant="solid"
                onClick={onSubmitFeedback}
              >
                {t('feedback.send')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default React.memo(FeedbackModal);
